import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, SecurityContext, TemplateRef, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {AffiliateExclusiveOption, MerchantAd, MerchantRelationshipAffiliate, Template, TemplateEntityGroup, TemplateRequest} from '../../core/dtos/api';
import {FormBuilder, Validators} from '@angular/forms';
import {DomSanitizer} from '@angular/platform-browser';
import { EmailApiService } from '../../core/services/api/email-api.service';
import { SnackBarService } from '../../core-services/snack-bar/snack-bar.service';
import {DashboardApiService} from '../../core/services/api/dashboard-api.service';
import {filter, first, map, shareReplay, switchMap} from 'rxjs/operators';
import {tinymceInit} from '../../core/tinymce';
import {combineLatest, Observable, of} from 'rxjs';
import {MerchantApiService} from '../../core/services/api/merchant-api.service';
import {environment} from '../../../environments/environment';
import {avantlinkHeaderPersonalizedTag} from '../email-partners/email-partners';
import {AreYouSureData, AreYouSureDialogComponent} from '../are-you-sure-dialog/are-you-sure-dialog.component';

@Component({
    templateUrl: './edit-email-templates-dialog.component.html',
    styleUrls: ['./edit-email-templates-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EditEmailTemplatesDialogComponent implements OnDestroy, OnInit {
  readonly applicationAcceptanceTemplateId = '2f17a084-4521-4d1d-9fda-77287c8d170e';
  displayEditor = false;
  currentAccount$ = this.dashboardApiService.currentAccount$.pipe(
      filter(currentAccount => currentAccount.entity_group_name === 'merchants' || currentAccount.entity_group_name === 'root')
  );
  usersAffiliates$ = combineLatest([of(this.data.template.template_id), this.currentAccount$]).pipe(
      switchMap(([templateId, currentAccount]) => (currentAccount.entity_group_name === 'merchants' && templateId === this.applicationAcceptanceTemplateId)
        ? this.dashboardApiService.usersAffiliates$ : of([]) as Observable<AffiliateExclusiveOption[]>));
  allMerchantAds$ = this.currentAccount$.pipe(
      switchMap(currentAccount => {
        if (currentAccount.entity_group_name === 'merchants' && this.data.template.template_id === this.applicationAcceptanceTemplateId) {
          return this.merchantApiService.getAllPagesOfMerchantAds(currentAccount.entity_id, {sort: 'ad_title', direction: 'ASC', status: 'active'});
        }
        return of([]);
      })
  );
  bannerMerchantAds$ = this.allMerchantAds$.pipe(
      map(merchantAds => merchantAds.filter(merchantAd => merchantAd.restricted_entity_id === null && merchantAd.ad_type === 'banner')),
      shareReplay({bufferSize: 1, refCount: true})
  );
  textMerchantAds$ = this.allMerchantAds$.pipe(
      map(merchantAds => merchantAds.filter(merchantAd => merchantAd.restricted_entity_id === null && merchantAd.ad_type === 'text')),
      shareReplay({bufferSize: 1, refCount: true})
  );
  designatedMerchantAds$ = this.allMerchantAds$.pipe(
      map(merchantAds => merchantAds.filter(merchantAd => merchantAd.restricted_entity_id !== null))
  );
  designatedMerchantAdsWithAffiliate$ = combineLatest([this.designatedMerchantAds$, this.usersAffiliates$]).pipe(
      map(([designatedMerchantAds, usersAffiliates]) => designatedMerchantAds
          .filter(merchantAd => usersAffiliates.find(userAffiliate => userAffiliate.affiliate_id === merchantAd.restricted_entity_id))
          .map(merchantAd => {
            let associatedAffiliate = usersAffiliates.find(userAffiliate => userAffiliate.affiliate_id === merchantAd.restricted_entity_id);
            return {...merchantAd, designatedAffiliate: associatedAffiliate} as MerchantAd & { designatedAffiliate: MerchantRelationshipAffiliate };
          })),
      shareReplay({bufferSize: 1, refCount: true})
  );

  init = tinymceInit;
  templateControls = {
    subjectLine: this.fb.control(this.data.template.template_subject, Validators.required),
    bodyEditor: this.fb.control(this.data.template.template_body, Validators.required),
    sentFromName: this.fb.control(this.data.template.email_from_name, Validators.required),
    sentFromEmail: this.fb.control(this.data.template.email_from, [Validators.required, Validators.email])
  };
  templateControlGroup = this.fb.group(
    this.templateControls
  );

  markBodyEditorFieldPristine$Sub = this.templateControls.bodyEditor.valueChanges.pipe(first()).subscribe(() => {
    this.templateControls.bodyEditor.markAsPristine();
  });

  backdropClicked$Sub = this.matDialogRef.backdropClick()
    .subscribe(() => {
      this.maybeClose();
    });
  constructor(public matDialog: MatDialog, @Inject(MAT_DIALOG_DATA) public data: {template: Template},
              private fb: FormBuilder, private merchantApiService: MerchantApiService, private domSanitizer: DomSanitizer,
              private emailApiService: EmailApiService, private snackBarService: SnackBarService, private detectChangesRef: ChangeDetectorRef,
              private matDialogRef: MatDialogRef<EditEmailTemplatesDialogComponent>, private dashboardApiService: DashboardApiService) {}

  onSaveClick() {
    let updateBody: TemplateRequest = {
      template_name: this.data.template.template_name,
      template_body: this.templateControls.bodyEditor.value,
      template_subject: this.templateControls.subjectLine.value,
      template_network: this.data.template.template_network,
      email_from: this.templateControls.sentFromEmail.value
    };
    if (this.templateControls.sentFromName.value !== null) {
      updateBody.email_from_name = this.templateControls.sentFromName.value;
    }

    let updateRequest$Sub = this.dashboardApiService.currentAccount$.pipe(
      first(),
      switchMap(currentAccount => {
        if (currentAccount.entity_group_name === 'root' || this.data.template.entity_id !== '00000000-0000-4000-a000-000000000000') {
          return this.emailApiService.updateEmailTemplate(this.data.template.entity_id,
            this.data.template.entity_group_name,
            this.data.template.template_id,
            updateBody);
        }
        // If it is a merchant/affiliate non-global (00000000-0000-4000-a000-000000000000 is global) template we need to create it.
        updateBody.template_type_id = this.data.template.template_type.template_type_id;
        updateBody.template_id = this.data.template.template_id;
        return this.emailApiService.createEmailTemplate(currentAccount.entity_id, currentAccount.entity_group_name as TemplateEntityGroup, updateBody);
      })
    ).subscribe({
      next: () => {
        this.snackBarService.openSnackBar('Saved');
        this.matDialogRef.close(true);
      },
      error: () => {
        this.snackBarService.openErrorSnackBar('There was a problem when updating the template.');
        this.matDialogRef.close(false);
      }
    });
  }

  onCancelClick() {
    this.maybeClose();
  }

  private maybeClose() {
    if (!this.templateControlGroup.dirty) {
      this.matDialogRef.close(false);
      return;
    }

    let discardDialogRef = this.matDialog.open<AreYouSureDialogComponent, AreYouSureData, boolean>(
      AreYouSureDialogComponent,
      {
        data: {
          header: 'Discard Unsaved Changes?',
          message: 'You have unsaved changes that will be lost.',
          confirmButtonText: 'OK',
          cancelButtonText: 'Keep Editing'
        } as AreYouSureData
      });
    discardDialogRef.afterClosed().subscribe((close) => {
      if (close) {
        this.matDialogRef.close(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.backdropClicked$Sub.unsubscribe();
  }

  ngOnInit(): void {
    let addEditorMenus$Sub = combineLatest([
      this.textMerchantAds$,
      this.bannerMerchantAds$,
      this.designatedMerchantAdsWithAffiliate$,
      this.currentAccount$
    ]).pipe(first()).subscribe(([textAds, bannerAds, designatedAdsWithAffiliate, currentAccount]) => {
      this.init['setup'] = editor => {
        // Add the personalized tag select on the editor.
        let displayedTemplateVariables = [avantlinkHeaderPersonalizedTag, ...this.data.template.variables.filter(variable => variable.selectable_variable)];
        let sortedDisplayedTemplateVariables = displayedTemplateVariables
          .sort((template1, template2) => template1.template_variable_name.localeCompare(template2.template_variable_name));
        if (sortedDisplayedTemplateVariables.length > 0) {
          editor.ui.registry.addMenuButton('personalizedMenu', {
            text: 'Personalized Tag',
            fetch: (callback) => {
              const items = displayedTemplateVariables.map(singleVariable => ({
                type: 'menuitem',
                text: singleVariable.template_variable_name,
                onAction: () => editor.insertContent(`{{${singleVariable.template_variable_key}}}`)
              }));
              callback(items);
            }
          });
        }
        if (currentAccount.entity_group_name === 'merchants' && this.data.template.template_id === this.applicationAcceptanceTemplateId
            && (textAds.length > 0 || bannerAds.length > 0 || designatedAdsWithAffiliate.length > 0)) {
          // Add the merchant ad select on the editor.
          editor.ui.registry.addMenuButton('merchantAdsMenu', {
            text: 'Insert Ad',
            fetch: (callback) => {
              let items = [];
              // Add the banner ad submenu.
              if (bannerAds.length > 0) {
                items.push({
                  type: 'nestedmenuitem',
                  text: 'Banner Ads',
                  getSubmenuItems: () => bannerAds.map(bannerAd => ({
                    type: 'menuitem',
                    text: `${bannerAd.ad_title} (${bannerAd.image_width} x ${bannerAd.image_height})`,
                    onAction: () => {
                      let htmlToBeInserted = '';
                      let imageElement = document.createElement('img');
                      imageElement.src = bannerAd.image_url;
                      imageElement.alt = bannerAd.ad_title;
                      imageElement.height = bannerAd.image_height;
                      imageElement.width = bannerAd.image_width;
                      htmlToBeInserted = imageElement.outerHTML + document.createElement('br').outerHTML;

                      let aElement = document.createElement('a');
                      aElement.href = `${environment.dashboardUrl}/ad-tools/ad-management?merchantAdId=${bannerAd.merchant_ad_id}`;
                      aElement.innerText = 'Get HTML';

                      htmlToBeInserted += aElement.outerHTML;
                      const sanitizedString = this.domSanitizer.sanitize(SecurityContext.HTML, htmlToBeInserted);
                      editor.insertContent(sanitizedString);
                    }
                  }))
                });
              }
              // Add the text ad submenu.
              if (textAds.length > 0) {
                items.push({
                  type: 'nestedmenuitem',
                  text: 'Text Ads',
                  getSubmenuItems: () => textAds.map(textAd => ({
                    type: 'menuitem',
                    text: textAd.ad_title,
                    onAction: () => {
                      let htmlToBeInserted = '';
                      htmlToBeInserted = `<p>${textAd.link_text}</p>`;

                      let aElement = document.createElement('a');
                      aElement.href = `${environment.dashboardUrl}/ad-tools/ad-management?merchantAdId=${textAd.merchant_ad_id}`;
                      aElement.innerText = 'Get HTML';

                      htmlToBeInserted += aElement.outerHTML;
                      const sanitizedString = this.domSanitizer.sanitize(SecurityContext.HTML, htmlToBeInserted);
                      editor.insertContent(sanitizedString);
                    }
                  }))
                });
              }
              // Add the designated ad submenu.
              if (designatedAdsWithAffiliate.length > 0) {
                items.push({
                  type: 'nestedmenuitem',
                  text: 'Designated Ads',
                  getSubmenuItems: () => designatedAdsWithAffiliate.map(designatedAd => ({
                    type: 'menuitem',
                    text: `${designatedAd.ad_title} (${designatedAd.designatedAffiliate.affiliate_name})`,
                    onAction: () => {
                      let htmlToBeInserted = '';
                      htmlToBeInserted = `<p>${designatedAd.link_text}</p>`;

                      let aElement = document.createElement('a');
                      aElement.href = `${environment.dashboardUrl}/ad-tools/ad-management?merchantAdId=${designatedAd.merchant_ad_id}`;
                      aElement.innerText = 'Get HTML';

                      htmlToBeInserted += aElement.outerHTML;
                      const sanitizedString = this.domSanitizer.sanitize(SecurityContext.HTML, htmlToBeInserted);
                      editor.insertContent(sanitizedString);
                    }
                  }))
                });
              }
              callback(items);
            }
          });
        }
      };
      this.displayEditor = true;
      this.detectChangesRef.detectChanges();
    });
  }
}
